import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNetworkingOptimized } from './useNetworkingOptimized';
import { useLoadingState } from './useLoadingState';
import { logInfo, logError } from '@/lib/productionLogger';

// Hook final production-ready pour networking
export const useNetworkingProductionReady = () => {
  const { 
    connectionRequests, 
    connections, 
    messages, 
    loading: networkingLoading,
    sendConnectionRequest: originalSendConnectionRequest,
    respondToConnectionRequest: originalRespondToConnectionRequest,
    sendMessage: originalSendMessage,
    networkingStats,
    fetchConnectionRequests,
    fetchMessages,
    fetchConnections
  } = useNetworkingOptimized();

  const { executeAsync, isLoading: actionLoading, error, setError } = useLoadingState();

  // Actions optimisées avec gestion d'erreur avancée
  const sendConnectionRequest = useCallback(async (targetUserId: string, message?: string) => {
    return executeAsync(async () => {
      logInfo('Sending connection request', { targetUserId, hasMessage: !!message });
      const result = await originalSendConnectionRequest(targetUserId, message);
      if (result) {
        logInfo('Connection request sent successfully');
      } else {
        logError('Failed to send connection request');
      }
      return result;
    });
  }, [executeAsync, originalSendConnectionRequest]);

  const respondToConnectionRequest = useCallback(async (requestId: string, status: 'accepted' | 'declined') => {
    return executeAsync(async () => {
      logInfo('Responding to connection request', { requestId, status });
      const result = await originalRespondToConnectionRequest(requestId, status);
      if (result) {
        logInfo('Connection request response processed successfully');
      } else {
        logError('Failed to process connection request response');
      }
      return result;
    });
  }, [executeAsync, originalRespondToConnectionRequest]);

  const sendMessage = useCallback(async (recipientId: string, content: string, subject?: string) => {
    return executeAsync(async () => {
      logInfo('Sending message', { recipientId, hasSubject: !!subject });
      const result = await originalSendMessage(recipientId, content, subject);
      if (result) {
        logInfo('Message sent successfully');
      } else {
        logError('Failed to send message');
      }
      return result;
    });
  }, [executeAsync, originalSendMessage]);

  // État global optimisé
  const isLoading = useMemo(() => networkingLoading || actionLoading, [networkingLoading, actionLoading]);

  // Statistiques enrichies
  const enhancedStats = useMemo(() => ({
    ...networkingStats,
    hasActivity: networkingStats.totalConnections > 0 || networkingStats.totalMessages > 0,
    needsAttention: networkingStats.pendingRequests > 0 || networkingStats.unreadMessages > 0
  }), [networkingStats]);

  // Données filtrées et optimisées
  const recentActivity = useMemo(() => {
    const recentMessages = messages
      .filter(msg => {
        const messageDate = new Date(msg.created_at);
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        return messageDate > sevenDaysAgo;
      })
      .slice(0, 5);

    const recentConnections = connections
      .slice(0, 3);

    return {
      messages: recentMessages,
      connections: recentConnections
    };
  }, [messages, connections]);

  return {
    // État de base
    connectionRequests,
    connections,
    messages,
    isLoading,
    error,
    
    // Actions optimisées
    sendConnectionRequest,
    respondToConnectionRequest,
    sendMessage,
    
    // Données enrichies
    networkingStats: enhancedStats,
    recentActivity,
    
    // Utilitaires
    refetch: useCallback(async () => {
      await Promise.all([
        fetchConnectionRequests(),
        fetchMessages(),
        fetchConnections()
      ]);
    }, [fetchConnectionRequests, fetchMessages, fetchConnections]),
    clearError: () => setError(null)
  };
};